/**
 * WCAG 2.x relative-luminance contrast, for warning quiz authors when their
 * chosen theme would be hard to read in the room. Advisory only — the server
 * accepts any valid hex; taste is the author's call, legibility is flagged.
 */

function channel(value: number): number {
  const srgb = value / 255;
  return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
}

function luminance(hex: string): number {
  const raw = hex.replace('#', '');
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function contrastRatio(hexA: string, hexB: string): number {
  const a = luminance(hexA);
  const b = luminance(hexB);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

export interface ThemeWarning {
  message: string;
}

/** The pairs that matter on stage: question text, bars, and the chosen paddle. */
export function themeContrastWarnings(theme: {
  textColor: string;
  backgroundColor: string;
  primaryColor: string;
}): ThemeWarning[] {
  const warnings: ThemeWarning[] = [];

  if (contrastRatio(theme.textColor, theme.backgroundColor) < 4.5) {
    warnings.push({
      message:
        'Text and background are close in brightness — questions will be hard to read from the back of the room.',
    });
  }

  if (contrastRatio(theme.primaryColor, theme.backgroundColor) < 3) {
    warnings.push({
      message: 'The primary color blends into the background — result bars will be hard to see.',
    });
  }

  return warnings;
}
